import React, { useMemo } from 'react';
import {
  LuBell,
  LuBuilding2,
  LuChevronRight,
  LuCreditCard,
  LuKeyRound,
  LuScrollText,
} from 'react-icons/lu';
import { useAuth } from '../AuthContext';
import { tenantDisplayName } from '../utils/businessType';
import { useLanguage } from '../i18n/LanguageContext';
import { firstDisplayNameFromUser, normalizeRole, roleLabel } from '../utils/userLocale';
import { isOnActiveTrial } from '../utils/tenantModules';
import './SettingsPages.css';

const OWNER_ROWS = [
  { key: 'business-settings', labelKey: 'settings.businessProfile', Icon: LuBuilding2, tone: 'teal' },
  { key: 'settings-notifications', labelKey: 'settings.notifications', Icon: LuBell, tone: 'amber' },
  { key: 'subscription', labelKey: 'settings.billing', Icon: LuCreditCard, tone: 'violet' },
  { key: 'security-password', labelKey: 'settings.password', Icon: LuKeyRound, tone: 'slate' },
  { key: 'security-logs', labelKey: 'settings.activityLog', Icon: LuScrollText, tone: 'slate' },
];

const STAFF_ROWS = [
  { key: 'settings-notifications', labelKey: 'settings.notifications', Icon: LuBell, tone: 'amber' },
  { key: 'security-password', labelKey: 'settings.password', Icon: LuKeyRound, tone: 'slate' },
];

function SettingsHubPage({ onNavigate }) {
  const { user } = useAuth();
  const { t, lang } = useLanguage();
  const isOwner = normalizeRole(user?.role) === 'owner';
  const rows = isOwner ? OWNER_ROWS : STAFF_ROWS;
  const onTrial = isOnActiveTrial(user);
  const displayName = firstDisplayNameFromUser(user);
  const businessName = tenantDisplayName(user);

  const initials = useMemo(() => {
    const s = (displayName || 'U').replace(/[^a-zA-Z]/g, '').slice(0, 2);
    return (s || 'U').toUpperCase();
  }, [displayName]);

  return (
    <div className="page-bleed settings-modern">
      <header className="settings-modern__head">
        <h1 className="settings-modern__title">{t('settings.title')}</h1>
        {onTrial ? (
          <span className="settings-modern__trial" role="status">
            {t('settings.trial')}
          </span>
        ) : null}
      </header>

      <div className="settings-modern__profile">
        {user?.profileUrl ? (
          <img src={user.profileUrl} alt="" className="settings-modern__avatar" />
        ) : (
          <span className="settings-modern__avatar settings-modern__avatar--initials" aria-hidden>
            {initials}
          </span>
        )}
        <div className="settings-modern__who">
          <strong>{displayName || 'User'}</strong>
          <span>
            {roleLabel(user?.role, lang)}
            {businessName ? ` · ${businessName}` : ''}
          </span>
        </div>
      </div>

      <nav className="settings-modern__group" aria-label="Settings">
        {rows.map(({ key, labelKey, Icon, tone }) => (
          <button key={key} type="button" className="settings-modern__row" onClick={() => onNavigate(key)}>
            <span className={`settings-modern__icon settings-modern__icon--${tone}`} aria-hidden>
              <Icon size={18} strokeWidth={2.25} />
            </span>
            <span className="settings-modern__label">{t(labelKey)}</span>
            <LuChevronRight className="settings-modern__chev" size={18} aria-hidden />
          </button>
        ))}
      </nav>

      <p className="settings-modern__foot">{t('settings.foot')}</p>
    </div>
  );
}

export default SettingsHubPage;
